import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Compass } from "lucide-react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { inr } from "@/lib/format";
import Shell from "@/components/Shell";

const MAX_POLLS = 12;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let attempts = 0, stop = false;
    const poll = async () => {
      if (stop) return;
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setState("paid");
          if (res.data.booking_id) {
            const b = await api.get(`/bookings/${res.data.booking_id}`);
            setBooking(b.data);
          }
          return;
        }
      } catch { setState("error"); return; }
      attempts += 1;
      if (attempts >= MAX_POLLS) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [sessionId]);

  return (
    <Shell footer={false}>
      <div className="max-w-lg mx-auto px-5 py-24 text-center">
        {state === "checking" && (
          <div data-testid="payment-checking">
            <Loader2 className="animate-spin text-accent mx-auto" size={40} />
            <h1 className="font-display text-3xl mt-6 text-foreground">Confirming your payment…</h1>
          </div>
        )}
        {state === "paid" && (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} data-testid="payment-success">
            <CheckCircle2 className="text-accent mx-auto" size={56} />
            <h1 className="font-display text-4xl mt-6 text-foreground">Booked & confirmed!</h1>
            <p className="text-muted-foreground mt-2">A confirmation has been sent to your email.</p>
            {booking && (
              <div className="mt-8 rounded-2xl border border-primary/10 bg-card p-6 text-left">
                <span className="text-xs font-semibold uppercase text-accent">{booking.type}</span>
                <h3 className="font-semibold text-lg text-foreground mt-1">{booking.title}</h3>
                <div className="mt-3 space-y-1.5 text-sm text-muted-foreground">
                  <p>Amount: <span className="text-foreground font-medium">{inr(booking.amount)}</span></p>
                  <p>Status: <span className="text-accent font-semibold uppercase text-xs">{booking.status}</span></p>
                </div>
              </div>
            )}
            <div className="flex gap-3 justify-center mt-8">
              <Link to="/my-bookings" className="rounded-full bg-primary text-primary-foreground px-6 py-3 font-semibold" data-testid="success-trips">My trips</Link>
              <Link to="/explore" className="inline-flex items-center gap-2 rounded-full border border-primary/20 px-6 py-3 font-semibold text-foreground"><Compass size={16} /> Explore more</Link>
            </div>
          </motion.div>
        )}
        {(state === "timeout" || state === "error") && (
          <div data-testid="payment-issue">
            <XCircle className="text-destructive mx-auto" size={48} />
            <h1 className="font-display text-3xl mt-6 text-foreground">{state === "timeout" ? "Still processing" : "Something went wrong"}</h1>
            <p className="text-muted-foreground mt-2">{state === "timeout" ? "Your payment is taking longer than expected — check My Trips shortly." : "We couldn't confirm this payment. If charged, contact support."}</p>
            <Link to="/explore" className="inline-block mt-8 rounded-full bg-primary text-primary-foreground px-6 py-3 font-semibold">Back to explore</Link>
          </div>
        )}
      </div>
    </Shell>
  );
}
